import {
  Box,
  Button,
  Card,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  makeStyles
} from '@material-ui/core'
import { DisplayGroupSortBy, DisplayGroupSortOrder } from '../types'
import React, { useCallback, useMemo, useState } from 'react'
import { SortDirection, SortableTableCell } from '../../../ui/SortableTableCell'

import { Alert } from '@material-ui/lab'
import CheckIcon from '@material-ui/icons/Check'
import { FlexHorizontal, Spacer } from '@vestaboard/installables'
import { FormattedDateTime } from '../../formatted-date-time'
import InfiniteScroll from 'react-infinite-scroller'
import { SearchInput } from '../../../ui/SearchInput'
import { TruncatedId } from '../../truncated-id'
import { useHistory } from 'react-router'
import { useSearchDisplayGroups } from '../hooks/useSearchDisplayGroups'

const PER_PAGE = 50

const useStyles = makeStyles({
  tableHeader: {
    whiteSpace: 'nowrap'
  },
  tableCell: {
    whiteSpace: 'nowrap'
  },
  onlineStatusDot: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginLeft: 8
  }
})

const getOnlineStatusColor = (onlineCount: number, totalCount: number) => {
  if (!totalCount || onlineCount === 0) {
    return '#f44336'
  }
  if (onlineCount === totalCount) {
    return '#4caf50'
  }
  return '#ff9800'
}

export const PairedArrays: React.FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const [searchTerm, setSearchTerm] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<DisplayGroupSortBy>(DisplayGroupSortBy.createdAt)
  const [sortDirection, setSortDirection] = useState<DisplayGroupSortOrder>(DisplayGroupSortOrder.desc)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const input = useMemo(
    () => ({
      searchTerm,
      sortBy,
      sortDirection,
      limit: PER_PAGE
    }),
    [searchTerm, sortBy, sortDirection]
  )

  const { data, loading, error, fetchMore } = useSearchDisplayGroups({ input })

  const result = data?.searchDisplayGroups.__typename === 'SearchDisplayGroupsV2' ? data.searchDisplayGroups : undefined
  const searchError =
    data?.searchDisplayGroups.__typename === 'DisplayGroupErrorV2' ? data.searchDisplayGroups.error : undefined

  const loadNextPage = useCallback(async () => {
    if (isLoadingMore || !result?.nextCursor) {
      return
    }

    setIsLoadingMore(true)
    return await fetchMore({
      updateQuery(previousQueryResult, options) {
        const previousResult = previousQueryResult.searchDisplayGroups
        const nextResult = options.fetchMoreResult?.searchDisplayGroups

        if (
          previousResult.__typename !== 'SearchDisplayGroupsV2' ||
          nextResult?.__typename !== 'SearchDisplayGroupsV2'
        ) {
          return previousQueryResult
        }

        return {
          searchDisplayGroups: {
            ...previousResult,
            nextCursor: nextResult.nextCursor,
            displayGroups: [...previousResult.displayGroups, ...nextResult.displayGroups]
          }
        }
      },
      variables: {
        input: {
          ...input,
          cursor: result?.nextCursor || null
        }
      }
    }).finally(() => {
      setIsLoadingMore(false)
    })
  }, [fetchMore, input, isLoadingMore, result])

  const displayGroups = result?.displayGroups || []

  return (
    <Box>
      <FlexHorizontal spaceBetween>
        <Button variant='contained' color='primary' onClick={() => history.push('/note-arrays/create')}>
          Create Note Array
        </Button>
        <SearchInput onSearch={setSearchTerm} />
      </FlexHorizontal>
      <Spacer size='large' />
      {loading && !displayGroups.length ? (
        <CircularProgress />
      ) : error || searchError ? (
        <Alert severity='error'>{searchError || 'There was an error loading the Note Arrays'}</Alert>
      ) : !displayGroups.length ? (
        <Alert severity='info'>There are no Note Arrays</Alert>
      ) : (
        <InfiniteScroll
          pageStart={0}
          loadMore={loadNextPage}
          hasMore={!!result?.nextCursor}
          loader={
            <Box key='loader'>
              <Spacer size='large' />
              <CircularProgress />
            </Box>
          }>
          <Card
            style={{
              maxWidth: 'calc(100vw - 290px)',
              overflowX: 'auto'
            }}>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader}>Group ID</TableCell>
                    <SortableTableCell
                      direction={SortDirection[sortDirection]}
                      active={sortBy === DisplayGroupSortBy.title}
                      width={200}
                      onClick={direction => {
                        setSortDirection(DisplayGroupSortOrder[direction])
                        setSortBy(DisplayGroupSortBy.title)
                      }}>
                      Title
                    </SortableTableCell>
                    <SortableTableCell
                      direction={SortDirection[sortDirection]}
                      active={sortBy === DisplayGroupSortBy.ownerName}
                      width={180}
                      onClick={direction => {
                        setSortDirection(DisplayGroupSortOrder[direction])
                        setSortBy(DisplayGroupSortBy.ownerName)
                      }}>
                      Owner
                    </SortableTableCell>
                    <SortableTableCell
                      direction={SortDirection[sortDirection]}
                      active={sortBy === DisplayGroupSortBy.ownerEmail}
                      width={200}
                      onClick={direction => {
                        setSortDirection(DisplayGroupSortOrder[direction])
                        setSortBy(DisplayGroupSortBy.ownerEmail)
                      }}>
                      Owner Email
                    </SortableTableCell>
                    <TableCell className={classes.tableHeader}>Dimensions</TableCell>
                    <TableCell className={classes.tableHeader}>Sync Delay</TableCell>
                    <TableCell className={classes.tableHeader}>Boards</TableCell>
                    <TableCell className={classes.tableHeader}>Firmware Version(s)</TableCell>
                    <SortableTableCell
                      direction={SortDirection[sortDirection]}
                      active={sortBy === DisplayGroupSortBy.plusExpiresAt}
                      width={150}
                      onClick={direction => {
                        setSortDirection(DisplayGroupSortOrder[direction])
                        setSortBy(DisplayGroupSortBy.plusExpiresAt)
                      }}>
                      Plus
                    </SortableTableCell>
                    <SortableTableCell
                      direction={SortDirection[sortDirection]}
                      active={sortBy === DisplayGroupSortBy.createdAt}
                      width={120}
                      onClick={direction => {
                        setSortDirection(DisplayGroupSortOrder[direction])
                        setSortBy(DisplayGroupSortBy.createdAt)
                      }}>
                      Created
                    </SortableTableCell>
                    <TableCell className={classes.tableHeader}>Lifecycle State</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayGroups.map(group => {
                    const onlineCount = group.boards.filter(
                      board => board?.onlineStatus?.toLocaleLowerCase() === 'online'
                    ).length

                    const firmwareVersions = Array.from(
                      new Set(group.boards.map(board => board.firmwareVersion).filter(Boolean))
                    )

                    return (
                      <TableRow key={group.id}>
                        <TableCell className={classes.tableCell}>
                          <TruncatedId value={group.id} path={`/note-arrays/${group.id}`} />
                        </TableCell>
                        <TableCell className={classes.tableCell}>{group.title || 'Untitled'}</TableCell>
                        <TableCell className={classes.tableCell}>{group.ownerName || ''}</TableCell>
                        <TableCell className={classes.tableCell}>{group.ownerEmail || ''}</TableCell>
                        <TableCell className={classes.tableCell}>
                          {group.rows} × {group.columns}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {Math.round((group.displayDelay || 0) / 1000)}s
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Box
                            component='span'
                            className={classes.onlineStatusDot}
                            style={{ backgroundColor: getOnlineStatusColor(onlineCount, group.boards.length) }}
                          />
                          &nbsp; {onlineCount} / {group.boards.length} online
                        </TableCell>
                        <TableCell className={classes.tableCell}>{firmwareVersions.join(', ')}</TableCell>
                        <TableCell className={classes.tableCell}>
                          {group.plusExpiresAt && group.plusExpiresAt > Date.now() ? (
                            <CheckIcon fontSize='small' />
                          ) : null}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <FormattedDateTime format='MM/DD/YYYY' value={group.createdAt} />
                        </TableCell>
                        <TableCell className={classes.tableCell}>{group.deletedAt ? 'Deleted' : 'Active'}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </InfiniteScroll>
      )}
    </Box>
  )
}
